// Word-level sentence builder for auto-generated captions: exact sentence
// starts from per-word timing, splitting on punctuation, then long pauses.

import { SENTENCE_END_RE as TERMINAL_PUNCTUATION } from './sentenceLang';

// Calibrated on vmEpmQNomw0: mid-sentence emphasis pauses run 900ms+, real
// sentence gaps cluster above 1.5s.
const PAUSE_THRESHOLD_MS = 1500;

const MAX_DURATION_S = 12;

// Stops a lone "OK." from fragmenting the flow.
const MIN_WORDS = 3;

// words[].t is ms from segment start; manual tracks (no words) give one entry per cue.
export function segmentsToWords(segments) {
  var entries = [];
  var lastEndMs = 0;

  (segments || []).forEach(function(seg) {
    var pStartMs = Math.round(seg.start * 1000);
    var pEndMs = pStartMs + Math.round((seg.dur || 0) * 1000);
    lastEndMs = Math.max(lastEndMs, pEndMs);

    if (!seg.words || seg.words.length === 0) {
      var text = (seg.text || '').trim();
      if (text) entries.push({ word: text, startMs: pStartMs });
      return;
    }

    var segStart = entries.length;
    seg.words.forEach(function(w) {
      if (w.word) {
        entries.push({ word: w.word, startMs: pStartMs + (w.t || 0) });
      }
    });
    // <s> words often lose the cue's punctuation ("guys" vs "guys?"); restore it.
    var cueWords = (seg.text || '').trim().split(/\s+/).filter(Boolean);
    var added = entries.length - segStart;
    if (added === cueWords.length) {
      for (var ci = 0; ci < cueWords.length; ci++) {
        var e = entries[segStart + ci];
        if (!TERMINAL_PUNCTUATION.test(e.word) && TERMINAL_PUNCTUATION.test(cueWords[ci])) {
          var m = cueWords[ci].match(TERMINAL_PUNCTUATION);
          e.word = e.word + m[0];
        }
      }
    }
  });

  return entries.map(function(e, i) {
    return {
      word: e.word,
      start: e.startMs / 1000,
      end: i + 1 < entries.length ? entries[i + 1].startMs / 1000 : lastEndMs / 1000
    };
  });
}

// end = next word's start (seconds).
export function parseTimedTextXml(xml) {
  var entries = [];
  var lastPEndMs = 0;

  var pRe = /<p\b([^>]*)>([\s\S]*?)<\/p>/g;
  var pm;
  while ((pm = pRe.exec(xml)) !== null) {
    var attrs = pm[1];
    // a="1" paragraphs duplicate timing for rolling display.
    if (/\ba="1"/.test(attrs)) continue;

    var tMatch = /(?:^|\s)t="(\d+)"/.exec(attrs);
    if (!tMatch) continue;
    var pStartMs = Number(tMatch[1]);

    var dMatch = /(?:^|\s)d="(\d+)"/.exec(attrs);
    var pDurMs = dMatch ? Number(dMatch[1]) : 0;
    lastPEndMs = Math.max(lastPEndMs, pStartMs + pDurMs);

    var body = pm[2];
    var sRe = /<s\b([^>]*)>([\s\S]*?)<\/s>/g;
    var sm;
    while ((sm = sRe.exec(body)) !== null) {
      var tOffMatch = /(?:^|\s)t="(\d+)"/.exec(sm[1]);
      var offset = tOffMatch ? Number(tOffMatch[1]) : 0;
      var word = decodeEntities(sm[2]).trim();
      if (word) {
        entries.push({ word: word, startMs: pStartMs + offset });
      }
    }
  }

  return entries.map(function(e, i) {
    return {
      word: e.word,
      start: e.startMs / 1000,
      end: i + 1 < entries.length ? entries[i + 1].startMs / 1000 : lastPEndMs / 1000
    };
  });
}

export function buildSentences(words) {
  var sentences = [];
  var group = [];

  function flush() {
    if (group.length === 0) return;
    var first = group[0];
    var last = group[group.length - 1];
    sentences.push({
      text: group.map(function(w) { return w.word; }).join(' '),
      start: first.start,
      end: last.end
    });
    group = [];
  }

  for (var i = 0; i < words.length; i++) {
    var w = words[i];
    if (!w.word) continue;

    if (group.length > 0) {
      var prev = group[group.length - 1];
      // Start-to-start: a paragraph's last word has no reliable end time.
      var gapMs = (w.start - prev.start) * 1000;
      var durationS = w.end - group[0].start;

      if ((gapMs >= PAUSE_THRESHOLD_MS && group.length >= MIN_WORDS) || durationS >= MAX_DURATION_S) {
        flush();
      }
    }

    group.push(w);

    if (group.length >= MIN_WORDS && TERMINAL_PUNCTUATION.test(w.word)) {
      flush();
    }
  }

  flush();
  return sentences;
}

function decodeEntities(s) {
  return s
    .replace(/&#(\d+);/g, function(_, n) { return String.fromCodePoint(Number(n)); })
    .replace(/&#x([0-9a-fA-F]+);/g, function(_, n) { return String.fromCodePoint(parseInt(n, 16)); })
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&');
}
